import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { ShortUrl } from '@air/api';
import { matchesAirror, NOT_FOUND } from '@air/errors';
import { reportErrorToBugsnag } from '@air/utils-error';

import { getShortUrlForCollectionKey } from '~/components/SharePrivateCollectionModal/hooks/queries/useGetShortUrlForCollection';
import { useRevalidateISR } from '~/hooks/useRevalidateISR';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

/**
 * This hook returns a function that you can pass a collection id to that will,
 * if it exists, revalidate the short url for that collection. You want to call this
 * function if any changes are made to the collection and you want those changes to be
 * reflected in the public collection. This function under the hood
 * kicks off a regeneration of the static files for the public collection.
 */
export const useRevalidatePublicCollection = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { revalidateISR } = useRevalidateISR();
  const client = useQueryClient();

  const getPublicCollectionShortId = useCallback(
    async (collectionId: string) => {
      const workspaceId = currentWorkspace?.id;
      if (!workspaceId) {
        throw new Error('No workspace id');
      }
      try {
        return await client.ensureQueryData({
          queryKey: getShortUrlForCollectionKey({ collectionId }),
          queryFn: () =>
            ShortUrl.getShortIdForCollection({
              workspaceId,
              id: collectionId,
            }),
        });
      } catch (error) {
        // if short url doesn't exist, just return null - we don't need to revalidate
        if (matchesAirror(error, NOT_FOUND)) {
          return null;
        }
        throw error;
      }
    },
    [currentWorkspace?.id, client],
  );

  const revalidatePublicCollection = useCallback(
    async (collectionId: string) => {
      try {
        const response = await getPublicCollectionShortId(collectionId);

        if (!response) {
          // if there is no short url for this collection, we don't need to revalidate
          return;
        }

        await revalidateISR({
          path: `/a/${response.id}`,
        });
      } catch (error) {
        reportErrorToBugsnag({
          error,
          context: 'Failed to revalidate public collection',
          metadata: { collectionId },
        });
      }
    },
    [getPublicCollectionShortId, revalidateISR],
  );

  return {
    revalidatePublicCollection,
  };
};
